import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';

const initialState = {
  albums: {}, 
  loading: false,
  error: null,
  pagination: {},
  selectedAlbum: null,
  publishRequests: {
    pagination: {},
    loading: true,
    data: {}
  }
};

/* ================== THUNKS ================== */

export const fetchAlbums = createAsyncThunk(
  'albums/fetch',
  async ({ page = 1, limit = 10, search = '', podcasterId }, { rejectWithValue }) => {
    try {
        const params = { page, limit, search };
        if (podcasterId) {
            params.podcasterId = podcasterId;
        }
        const response = await axiosInstance.get('/api/admin/albums', { params });
        return {
            data: response.data?.data?.records || [],
            pagination: response.data?.data?.pagination || {},
        };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const createAlbum = createAsyncThunk(
  'albums/create',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/api/admin/albums', 
        payload
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const fetchAlbumById = createAsyncThunk(
  'albums/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/admin/albums/${id}`);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const fetchAlbumBySlug = createAsyncThunk(
  'albums/fetchBySlug',
  async (slug, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/admin/albums/slug/${slug}`);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const updateAlbum = createAsyncThunk(
  'albums/update',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/admin/albums/${id}`, formData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const deleteAlbum = createAsyncThunk(
  'albums/delete',
  async ({ id }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/admin/albums/${id}`);
      return { id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const scheduleAlbum = createAsyncThunk(
  'albums/scheduleRequest',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/admin/albums/${id}/schedule`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);


export const deleteScheduleRequest = createAsyncThunk(
  'albums/deleteScheduleRequest',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/api/admin/albums/${id}/schedule`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const publishAlbumRequest = createAsyncThunk(
  'albums/publishRequest',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/admin/albums/${id}/publish`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getAlbumPublishRequests = createAsyncThunk(
  'albums/getPublishRequests',
  async ({ type = "album", page = 1, limit = 10, search = '' }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/api/admin/albums/publish-requests', {
            params: { type, page, limit, search }
        });
        return {
            data: response.data?.data?.records || [],
            pagination: response.data?.data?.pagination || {},
        };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const publishRequestActionSingle = createAsyncThunk(
  'albums/publishRequestActionSingle',
  async ({ id, requestId, payload}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/admin/albums/publish-requests/${requestId}/action`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const publishRequestActionBulk = createAsyncThunk(
  'albums/publishRequestActionBulk',
  async ({ id, requestId, payload}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/admin/albums/publish-requests/bulk-action`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

/* ================== SLICE ================== */

const albumSlice = createSlice({
  name: 'albums',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
        /* FETCH */
        .addCase(fetchAlbums.pending, (state) => {
            state.loading = true;
        })
        .addCase(fetchAlbums.fulfilled, (state, action) => {
            const { data, pagination } = action.payload;
            state.loading = false;
            state.pagination = pagination;
            state.albums = data;
        })
        .addCase(fetchAlbums.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })

        /* CREATE */
        .addCase(fetchAlbumById.fulfilled, (state, action) => {
            state.loading = false;
            state.selectedAlbum = action.payload;
        })
        .addCase(fetchAlbumById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })

        .addCase(fetchAlbumBySlug.fulfilled, (state, action) => {
            state.loading = false;
            state.selectedAlbum = action.payload;
        })
        .addCase(fetchAlbumBySlug.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })

        /* UPDATE */
        .addCase(updateAlbum.fulfilled, (state, action) => {        
            console.log(state,'state');
        })
        .addCase(updateAlbum.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })

        .addCase(scheduleAlbum.fulfilled, (state, action) => {
            console.log(state,'state');
        })

        .addCase(publishAlbumRequest.fulfilled, (state, action) => {
            console.log(state,'state');
        })

        .addCase(deleteScheduleRequest.fulfilled, (state, action) => {
            console.log(state,'state');
        })

        .addCase(getAlbumPublishRequests.pending, (state) => {
            state.publishRequests.loading = true;
        })
        .addCase(getAlbumPublishRequests.fulfilled, (state, action) => {
            const { data, pagination } = action.payload;
            state.publishRequests.loading = false;
            state.publishRequests.pagination = pagination;
            state.publishRequests.data = data;
        })
        .addCase(getAlbumPublishRequests.rejected, (state, action) => {
            state.publishRequests.loading = false;
            state.error = action.payload;
        })

        .addCase(publishRequestActionSingle.fulfilled, (state, action) => {
            console.log(state,'state');
        })

        .addCase(publishRequestActionBulk.fulfilled, (state, action) => {
            console.log(state,'state');
        })

      /* DELETE */
      .addCase(deleteAlbum.fulfilled, (state, action) => {
        state.albums = state.albums.filter((p) => p._id !== action.payload);
      });
  }
});

export default albumSlice.reducer;